import type { CatalogueItemGroup } from '@/models/Catalogue';

/**
 * Get the full hierarchical path of a catalogue group
 */
export const getCatalogueGroupPath = (
  group: CatalogueItemGroup,
  allGroups: CatalogueItemGroup[],
): string => {
  if (!group.parentUuid) {
    return group.name;
  }

  const parent = allGroups.find((g) => g.uuid === group.parentUuid);
  if (!parent) {
    return group.name;
  }

  return `${getCatalogueGroupPath(parent, allGroups)} > ${group.name}`;
};

/**
 * Get the hierarchy level (depth) of a catalogue group
 */
export const getCatalogueGroupLevel = (
  group: CatalogueItemGroup,
  allGroups: CatalogueItemGroup[],
): number => {
  if (!group.parentUuid) {
    return 0;
  }

  const parent = allGroups.find((g) => g.uuid === group.parentUuid);
  if (!parent) {
    return 0;
  }

  return getCatalogueGroupLevel(parent, allGroups) + 1;
};

/**
 * Flatten a hierarchical catalogue group structure into a flat array
 */
export const getAllCatalogueGroupsFlat = (
  groups: CatalogueItemGroup[],
): CatalogueItemGroup[] => {
  if (!groups || !Array.isArray(groups)) {
    return [];
  }

  const result: CatalogueItemGroup[] = [];

  const flatten = (groupList: CatalogueItemGroup[]) => {
    if (!groupList || !Array.isArray(groupList)) {
      return;
    }

    groupList.forEach((group) => {
      result.push(group);
      if (group.children && group.children.length > 0) {
        flatten(group.children);
      }
    });
  };

  flatten(groups);
  return result;
};

/**
 * Build a hierarchical catalogue group structure from a flat array
 */
export const buildCatalogueGroupHierarchy = (
  flatGroups: CatalogueItemGroup[],
): CatalogueItemGroup[] => {
  if (!flatGroups || !Array.isArray(flatGroups)) {
    return [];
  }

  const groupMap = new Map<string, CatalogueItemGroup>();
  const rootGroups: CatalogueItemGroup[] = [];

  // First, create a map of all groups
  flatGroups.forEach((group) => {
    groupMap.set(group.uuid, { ...group, children: [] });
  });

  // Then, build the hierarchy
  flatGroups.forEach((group) => {
    const groupWithChildren = groupMap.get(group.uuid);
    if (!groupWithChildren) {
      return;
    }

    if (group.parentUuid) {
      const parent = groupMap.get(group.parentUuid);
      if (parent) {
        parent.children.push(groupWithChildren);
      }
    } else {
      rootGroups.push(groupWithChildren);
    }
  });

  return rootGroups;
};

/**
 * Check if selecting a catalogue group as parent would create a circular reference
 */
export const wouldCreateCatalogueCircularReference = (
  potentialParentUuid: string,
  currentGroupUuid: string,
  allGroups: CatalogueItemGroup[],
): boolean => {
  if (potentialParentUuid === currentGroupUuid) {
    return true; // Self-reference
  }

  // Check if the potential parent is a descendant of the current group
  const checkIfDescendant = (
    groupUuid: string,
    targetUuid: string,
  ): boolean => {
    const group = allGroups.find((g) => g.uuid === groupUuid);
    if (!group || !group.parentUuid) {
      return false;
    }

    if (group.parentUuid === targetUuid) {
      return true; // Found the target as parent
    }

    return checkIfDescendant(group.parentUuid, targetUuid);
  };

  return checkIfDescendant(potentialParentUuid, currentGroupUuid);
};

/**
 * Filter out catalogue groups that would create circular references
 */
export const getAvailableCatalogueParents = (
  groups: CatalogueItemGroup[],
  currentGroupUuid?: string,
): CatalogueItemGroup[] => {
  if (!groups || !Array.isArray(groups)) {
    return [];
  }

  if (!currentGroupUuid) {
    return groups;
  }

  const allGroups = getAllCatalogueGroupsFlat(groups);
  return groups.filter((group) => {
    // Exclude groups that would create circular references
    return !wouldCreateCatalogueCircularReference(
      group.uuid,
      currentGroupUuid,
      allGroups,
    );
  });
};

/**
 * Composable that provides catalogue group hierarchy utilities
 */
export const useCatalogueGroupHierarchy = () => {
  return {
    getCatalogueGroupPath,
    getCatalogueGroupLevel,
    getAllCatalogueGroupsFlat,
    buildCatalogueGroupHierarchy,
    wouldCreateCatalogueCircularReference,
    getAvailableCatalogueParents,
  };
};
